import { useForm, Link, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { InertiaSharedProps } from "@/types/inertia";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Info } from "lucide-react";

type ResetPasswordForm = {
  user: {
    password: string;
    password_confirmation: string;
    reset_password_token: string;
  };
};

interface ResetPasswordProps {
  reset_password_token: string;
  errors?: Record<string, string>;
}

export default function ResetPassword({
  reset_password_token,
  errors = {},
}: ResetPasswordProps) {
  const {
    data,
    setData,
    put,
    processing,
    errors: formErrors,
  } = useForm<ResetPasswordForm>({
    user: {
      password: "",
      password_confirmation: "",
      reset_password_token: reset_password_token || "",
    },
  });
  const { flash } = usePage<InertiaSharedProps>().props;

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    put("/reset_password");
  };

  return (
    <AuthLayout
      title="Set New Password"
      description="Choose a new secure password"
    >
      <form onSubmit={submit}>
        <div className="flex flex-col gap-6">
          {/* Show flash messages */}
          {flash?.alert && (
            <Alert variant="destructive" className="bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{flash.alert}</AlertDescription>
            </Alert>
          )}
          {flash?.notice && (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                {flash.notice}
              </AlertDescription>
            </Alert>
          )}

          {/* Show prop errors (for backwards compatibility) */}
          {errors && Object.keys(errors).length > 0 && (
            <Alert variant="destructive" className="bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {errors.reset_password_token ||
                  errors.password ||
                  "Please check the information provided."}
              </AlertDescription>
            </Alert>
          )}

          <FormField
            id="password"
            label="New Password"
            type="password"
            placeholder="••••••••"
            value={data.user.password}
            onChange={(value) => setData("user.password", value)}
            error={formErrors["user.password"]}
            required
            autoComplete="new-password"
          />

          <FormField
            id="password_confirmation"
            label="Confirm New Password"
            type="password"
            placeholder="••••••••"
            value={data.user.password_confirmation}
            onChange={(value) => setData("user.password_confirmation", value)}
            error={formErrors["user.password_confirmation"]}
            required
            autoComplete="new-password"
          />

          {/* Hidden field for reset token */}
          <input
            type="hidden"
            name="user[reset_password_token]"
            value={data.user.reset_password_token}
          />

          <div className="flex flex-col gap-3 pt-2">
            <Button
              type="submit"
              disabled={processing}
              variant="racing"
              size="lg"
              className="w-full shadow-xl"
            >
              {processing ? "Updating Password..." : "Update Password"}
            </Button>
          </div>
        </div>

        <div className="mt-6 text-center text-sm">
          <span className="text-gray-600">Remember your password?</span>{" "}
          <Link
            href="/login"
            className="text-orange-600 hover:text-orange-700 font-bold hover:underline"
          >
            Sign In
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
